import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress } from '@mui/material';
import CitizenAdvisorShell from '../../components/citizen/CitizenAdvisorShell';
import { loadPermitPublicDemoDefinition } from '../../data/interviewFlowPermitDemo';
import {
  fetchJurisdictionPackage,
  portalsFromPackage,
} from '../../data/sfAdvisorIntake';
import useResolvedJurisdiction from '../../hooks/useResolvedJurisdiction';
import { trackDiscovery } from '../../utils/discoverySession';
import {
  defaultsFromSearchParams,
  packageIdFromSearchParams,
} from '../../utils/citizenAdvisorQueryPrefill';

/**
 * Citizen portal shells — jurisdiction from vanity Host or SF marketing default.
 */
export default function StudioV2PermitPortalPage({
  basePath = '/v2/demo/permit',
  defaultPackageId = 'san-francisco',
} = {}) {
  const { portal } = useParams();
  const [searchParams] = useSearchParams();
  const queryDefaults = defaultsFromSearchParams(searchParams);
  const queryCity = packageIdFromSearchParams(searchParams);
  const resolved = useResolvedJurisdiction({ fallbackPackageId: defaultPackageId });
  const packageId = queryCity
    || (resolved.matched ? resolved.packageId : null)
    || resolved.packageId
    || defaultPackageId;

  const [definition, setDefinition] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [portals, setPortals] = useState({});
  const [variant, setVariant] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (resolved.loading) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const loaded = await fetchJurisdictionPackage(packageId);
        if (cancelled) return;
        setPkg(loaded);
        const portalMap = portalsFromPackage(loaded);
        setPortals(portalMap);
        const v = portalMap[portal];
        if (!v) {
          setVariant(null);
          setDefinition(null);
          return;
        }
        setVariant(v);
        const def = await loadPermitPublicDemoDefinition({
          portalId: v.id,
          pkg: loaded,
          jurisdictionId: loaded.packageId,
          theme: v.theme,
          defaults: { ...(v.defaults || {}), ...queryDefaults },
        });
        if (!cancelled) {
          setDefinition(def);
          trackDiscovery('portal_view', {
            packageId: loaded.packageId,
            portalId: v.id,
          });
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load jurisdiction package');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [portal, packageId, resolved.loading, searchParams]);

  const cityQs = queryCity ? `?city=${encodeURIComponent(queryCity)}` : '';
  const pathFor = (id) => `${id ? `${basePath}/${id}` : basePath}${cityQs}`;

  const portalLinks = useMemo(() => {
    const list = Object.values(portals);
    const hasLiveZoning = (pkg?.parcelAdapter || pkg?.intake?.parcelAdapter) === 'sf-planning-gis'
      || packageId === 'san-francisco';
    const zoningTo = queryCity
      ? `/zoning-check?city=${encodeURIComponent(queryCity)}`
      : '/zoning-check';
    return [
      { to: pathFor(null), label: 'Advisor', active: false },
      ...list.map((v) => ({
        to: pathFor(v.id),
        label: v.label,
        active: v.id === portal,
      })),
      ...(hasLiveZoning ? [{ to: zoningTo, label: 'Zoning', active: false }] : []),
    ];
  }, [portals, portal, basePath, pkg, packageId, queryCity, cityQs]);

  if (!resolved.loading && !loading && !variant) {
    return <Navigate to={basePath} replace />;
  }

  return (
    <Box sx={{ height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' }}>
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}
      {(resolved.loading || loading) && (
        <Box sx={{
          height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      {!loading && !resolved.loading && definition && (
        <CitizenAdvisorShell
          definition={definition}
          jurisdictionPackage={pkg}
          portalVariant={variant}
          mongoId={definition.id}
          portalLinks={portalLinks}
        />
      )}
    </Box>
  );
}
